import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Form, Input, Text, Title, dialogService, toastService } from '@thefarmersfront/kitchen';

import { BrandLogo, Bullet, InputLabel } from '~/components/common';

import { usePatchUserModifyPassword } from '~/hooks/auth';
import { authDBService } from '~/services';
import { useLoadingStore } from '~/store';
import {
  validateAuthPassword,
  validateConsecutiveKeyboardPattern,
  validateLetterRepeatThreeTimes,
  validateNoSpace,
} from '~/utils';

import { CHANGE_PASSWORD, CHANGE_PASSWORD_INFO_TEXT, PATH } from '~/constants';

import type { TypeErrorResponseData, TypeInputState, TypeInputStatus } from '~/types/common.types';

import styles from './ChangePasswordPage.module.scss';

const ChangePasswordPage = () => {
  const { patchUserModifyPassword } = usePatchUserModifyPassword();
  const { setIsLoading } = useLoadingStore();
  const navigate = useNavigate();

  const [loginId, setLoginId] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const [currentPasswordState, setCurrentPasswordState] = useState<TypeInputState>({
    status: 'default',
  });
  const [newPasswordState, setNewPasswordState] = useState<TypeInputState>({ status: 'default' });
  const [newPasswordConfirmState, setNewPasswordConfirmState] = useState<TypeInputState>({
    status: 'default',
  });

  useEffect(() => {
    const initUserInfo = async () => {
      const loginInfo = await authDBService.getChangePasswordInfo();
      const id = loginInfo?.login_id ?? '';

      if (!id) {
        dialogService.addDialog({
          type: 'alert',
          children: <span>잘못된 접근 경로입니다.</span>,
          cancelText: '확인',
          onConfirm: () => navigate(PATH.LOGIN),
        });
      }

      setLoginId(id);
    };

    initUserInfo();
  }, []);

  const handleClickPreviousButton = async () => {
    await authDBService.removeChangePasswordInfo();

    navigate(PATH.LOGIN);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setAllFieldsStatusAtOnce('default');

    const isAllFieldsFilled = currentPassword && newPassword && newPasswordConfirm;
    if (!isAllFieldsFilled) {
      validateEmptyFields();

      return;
    }

    const isNewPasswordAndConfirmSame = newPassword === newPasswordConfirm;
    if (!isNewPasswordAndConfirmSame) {
      toastService.addToast({
        type: 'error',
        message: '변경 비밀번호와 일치하지 않습니다.',
      });
      setNewPasswordConfirmState({ status: 'error' });

      return;
    }

    const isPasswordValid =
      validateNoSpace(newPassword) &&
      !validateLetterRepeatThreeTimes(newPassword) &&
      !validateConsecutiveKeyboardPattern(newPassword) &&
      validateAuthPassword(newPassword);

    if (!isPasswordValid) {
      setNewPasswordState({ status: 'error' });
      setNewPasswordConfirmState({ status: 'error' });

      toastService.addToast({
        type: 'error',
        message: '사용할 수 없는 비밀번호입니다.',
      });

      return;
    }

    patchChangePassword();
  };

  const patchChangePassword = async () => {
    try {
      setIsLoading(true);

      await patchUserModifyPassword({
        loginId,
        currentPassword,
        newPassword,
      });

      toastService.addToast({
        message: '성공적으로 변경되었습니다.\n다시 로그인해주세요.',
      });

      await authDBService.removeChangePasswordInfo();

      navigate(PATH.LOGIN);
    } catch (error) {
      const errorCode = (error as TypeErrorResponseData['error'])?.code;

      switch (errorCode) {
        case CHANGE_PASSWORD.WRONG_CURRENT_PASSWORD:
          setCurrentPasswordState({ status: 'error' });
          break;
        case CHANGE_PASSWORD.SAME_WITH_CURRENT_PASSWORD:
        case CHANGE_PASSWORD.SAME_WITH_PREVIOUS_PASSWORD:
        case CHANGE_PASSWORD.SAME_WITH_EMAIL:
          setNewPasswordState({ status: 'error' });
          setNewPasswordConfirmState({ status: 'error' });
          break;
        default:
          break;
      }

      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || '다시 시도해 주세요.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setAllFieldsStatusAtOnce = (status: TypeInputStatus) => {
    setCurrentPasswordState({ status });
    setNewPasswordState({ status });
    setNewPasswordConfirmState({ status });
  };

  const validateEmptyFields = () => {
    if (!currentPassword) {
      setCurrentPasswordState({
        status: 'error',
        message: '현재 비밀번호를 입력해 주세요.',
      });
    }

    if (!newPassword) {
      setNewPasswordState({
        status: 'error',
        message: '새로운 비밀번호를 입력해 주세요.',
      });
    }

    if (!newPasswordConfirm) {
      setNewPasswordConfirmState({
        status: 'error',
        message: '새로운 비밀번호를 한번 더 입력해 주세요.',
      });
    }
  };

  return (
    <div className={styles['change-password-page-container']}>
      <div className={styles['change-password-page-wrapper']}>
        <BrandLogo width={147} height={19} className={styles['change-password-logo']} />
        <Title size="accessibility1">비밀번호 변경</Title>
        <Form className={styles['change-password-form']} onSubmit={handleSubmit}>
          <InputLabel name="아이디" htmlFor="id">
            <Input value={loginId} type="text" disabled id="user-id" />
          </InputLabel>
          <InputLabel name="현재 비밀번호" htmlFor="current-password">
            <Input
              id="current-password"
              placeholder="현재 비밀번호를 입력해 주세요."
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              status={currentPasswordState.status}
              validationMessage={currentPasswordState.message ?? ''}
              onFocus={() => setCurrentPasswordState({ status: 'default' })}
            />
          </InputLabel>
          <div className={styles['password-input-wrapper']}>
            <InputLabel name="변경 비밀번호" htmlFor="new-password">
              <Input
                id="new-password"
                placeholder="새로운 비밀번호를 입력해 주세요."
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                status={newPasswordState.status}
                validationMessage={newPasswordState.message ?? ''}
                onFocus={() => setNewPasswordState({ status: 'default' })}
              />
            </InputLabel>
            <Input
              id="new-password-confirm"
              placeholder="새로운 비밀번호를 한 번 더 입력해 주세요."
              type="password"
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              status={newPasswordConfirmState.status}
              validationMessage={newPasswordConfirmState.message ?? ''}
              onFocus={() => setNewPasswordConfirmState({ status: 'default' })}
            />
          </div>
          <div className={styles['change-password-form-info']}>
            {CHANGE_PASSWORD_INFO_TEXT.map((text) => (
              <PasswordChangeInfoText key={text} text={text} />
            ))}
          </div>
          <div className={styles['button-container']}>
            <Button color="tertiary" variant="outlined" onClick={handleClickPreviousButton}>
              이전으로
            </Button>
            <Button color="secondary" type="submit">
              비밀번호 변경
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

const PasswordChangeInfoText = ({ text }: { text: string }) => {
  return (
    <div className={styles['change-password-form-info-text']}>
      <Text size="small" color="secondary">
        <Bullet fill="#BCC4CC" />
        {text}
      </Text>
    </div>
  );
};

export default ChangePasswordPage;
